import React, { useCallback, useEffect, useMemo } from "react";
import usePromise from "../utils/hooks/usePromise";
import { services } from "../services";
import {
  District,
  Gender,
  OnboardingStatus,
  State,
} from "@digital-girls-hub/shared-resources/models/db";
import Loader from "../assets/animated-images/loader";
import IndiaMap from "./CountryStats/IndiaMap";
import { useNavigate } from "react-router-dom";
import _, { isUndefined } from "lodash";
import Stats from "./CountryStats/Stats";
import { objectToQueryString, queryStringToObject } from "../utils/query";
import z from "zod";
import { useAuth } from "../utils/hooks/useAuth";
import AdminLayout from "../layouts/AdminLayout";
import { isStateAdmin } from "../utils/roles";

export type CountryStats = {
  year: string;
  month: string;
  onboardingStatus: OnboardingStatus;
  gender: Gender;
  onboardingCount: number;
  courseCompletionCount: number;
};

export type StateOverviewStat = {
  stateId: number;
  stateName: string;
  stateLimit: number;
  vleCount: number;
  learnerCount: number;
  skilledCount: number;
  placedCount: number;
  onboardingCount: number;
  courseCompletionCount: number;
};

export type DateParam = {
  month: number;
  year: number;
};

export const StatParamsSchema = z.object({
  stateId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  dateRange: z
    .object({
      from: z
        .object({
          month: z.coerce.number(),
          year: z.coerce.number(),
        })
        .optional(),
      to: z
        .object({
          month: z.coerce.number(),
          year: z.coerce.number(),
        })
        .optional(),
    })
    .optional(),
});

export type StatsParams = z.infer<typeof StatParamsSchema>;

export type StatsParamsSetter = <F extends keyof StatsParams>(
  field: F
) => (value: StatsParams[F]) => void;

export default function CountryStatsPage() {
  const navigate = useNavigate();
  const { admin, role } = useAuth();
  const stateAdmin = isStateAdmin(role);
  const forcedStateId =
    stateAdmin && admin?.stateId ? Number(admin.stateId) : undefined;

  /*




  Parsing and setting stats parameters




  */
  const statParams = useMemo<StatsParams>(() => {
    const currentState = queryStringToObject(window.location.search.slice(1));

    return StatParamsSchema.parse(
      _.merge(
        {
          dateRange: {
            from: {
              month: 6,
              year: 2025,
            },
            to: {
              month: new Date().getMonth() + 1,
              year: new Date().getFullYear(),
            },
          },
        },
        currentState
      )
    );
  }, [window.location.search]);

  /*




  Fetching states and districts data




  */
  const {
    result: states,
    error: fetchStatesErr,
    loading: loadingStates,
  } = usePromise(
    {
      fn: async () =>
        services.requestSvc
          .get<State[]>("/states", { withCredentials: true })
          .then((result) => {
            if (forcedStateId)
              return result.filter((s) => Number(s.id) === forcedStateId);
            return result;
          }),
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [forcedStateId]
  );

  const {
    result: districts,
    error: fetchDistrictsErr,
    loading: loadingDistricts,
  } = usePromise(
    {
      fn: async () => {
        if (statParams.stateId) {
          return services.requestSvc.get<District[]>(
            "/districts-by-state-id?stateId=" + Number(statParams.stateId),
            { withCredentials: true }
          );
        } else return [];
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [statParams.stateId]
  );

  /*




  Fetching stats




  */
  const {
    result: stateOverviewStats,
    error: fetchStateOverviewStatsError,
    loading: loadingStateOverviewStats,
  } = usePromise(
    {
      fn: async () =>
        services.requestSvc
          .get<StateOverviewStat[]>("/get-states-overview-stats", {
            withCredentials: true,
          })
          .then((result) => {
            return {
              totalOnboardingCount: result.reduce(
                (sum, cur) => sum + (cur.onboardingCount || 0),
                0
              ),
              totalCourseCompletionCount: result.reduce(
                (sum, cur) => sum + (cur.courseCompletionCount || 0),
                0
              ),
              result,
            };
          }),
      runOnMount: { args: [] },
    },
    []
  );

  const {
    result: countryStats,
    error: fetchCountryStatsErr,
    loading: loadingCountryStats,
  } = usePromise(
    {
      fn: async () => {
        const queryString = new URLSearchParams(
          _.omitBy(statParams, isUndefined) as unknown as undefined
        ).toString();

        const result = await services.requestSvc.get<CountryStats[]>(
          "/get-stats-by-states?" + queryString,
          { withCredentials: true }
        );

        const dateRangeLow = statParams.dateRange?.from;
        const dateRangeHigh = statParams.dateRange?.to;

        const filteredResult = result.filter((r) => {
          const fallsBehind =
            dateRangeLow &&
            dateRangeLow.year * 100 + dateRangeLow.month >
              Number(r.year) * 100 + Number(r.month);
          const fallsAhead =
            dateRangeHigh &&
            dateRangeHigh.year * 100 + dateRangeHigh.month <
              Number(r.year) * 100 + Number(r.month);
          return !fallsBehind && !fallsAhead;
        });

        return filteredResult;
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [statParams, states, districts]
  );

  /*



  Misc



  */

  const setStatsParams = useCallback<StatsParamsSetter>(
    (field) => (value) => {
      const newStatParams = _.omitBy(
        {
          ...statParams,
          [field]: value || undefined,
        },
        isUndefined
      ) as StatsParams;

      if (forcedStateId) {
        newStatParams.stateId = forcedStateId;
      }

      const district = districts?.find(
        (d) => Number(d.id) === statParams.districtId
      );
      if (district && Number(district.stateId) !== newStatParams.stateId) {
        newStatParams.districtId = undefined;
      }

      console.log("New stat params", newStatParams);

      navigate({ search: objectToQueryString(newStatParams) });
    },
    [navigate, statParams, districts, forcedStateId]
  );

  useEffect(() => {
    if (forcedStateId && statParams.stateId !== forcedStateId) {
      setStatsParams("stateId")(forcedStateId);
    }
  }, [forcedStateId, setStatsParams, statParams.stateId]);

  const visibleStateOverviewStats = useMemo(() => {
    if (!stateOverviewStats) return undefined;
    if (!forcedStateId) return stateOverviewStats.result;
    return stateOverviewStats.result.filter(
      (stat) => Number(stat.stateId) === forcedStateId
    );
  }, [stateOverviewStats, forcedStateId]);

  const overviewTotals = useMemo(() => {
    if (!stateOverviewStats || !visibleStateOverviewStats) return undefined;
    if (!forcedStateId) {
      return {
        totalOnboardingCount: stateOverviewStats.totalOnboardingCount,
        totalCourseCompletionCount: stateOverviewStats.totalCourseCompletionCount,
      };
    }
    return {
      totalOnboardingCount: visibleStateOverviewStats.reduce(
        (sum, cur) => sum + (cur.onboardingCount || 0),
        0
      ),
      totalCourseCompletionCount: visibleStateOverviewStats.reduce(
        (sum, cur) => sum + (cur.courseCompletionCount || 0),
        0
      ),
    };
  }, [stateOverviewStats, visibleStateOverviewStats, forcedStateId]);

  return (
    <AdminLayout title="Country Stats">
      {loadingCountryStats && loadingStates ? (
        <Loader />
      ) : stateOverviewStats && states && overviewTotals ? (
        <div className="flex flex-col md:flex-row w-full min-h-[600px] overflow-auto rounded-lg border border-gray-200 bg-white">
          <div className="w-full md:w-1/2 flex flex-col justify-start gap-y-5">
            <div className="w-full grid grid-cols-1 md:grid-cols-2">
              <div className="px-4 py-3 bg-gray-100 flex items-center justify-center gap-x-2 border-b md:border-b-0 md:border-r border-gray-200">
                <span className="font-bold text-lg">Total Learners:</span>
                <span className="font-bold text-2xl text-blue">
                  {overviewTotals.totalOnboardingCount}
                </span>
                <span className="font-semibold">
                  ({forcedStateId ? states?.[0]?.name : "India"})
                </span>
              </div>
              <div className="px-4 py-3 bg-gray-100 flex items-center justify-center gap-x-2">
                <span className="font-bold text-lg">Total Skilled:</span>
                <span className="font-bold text-2xl text-blue">
                  {overviewTotals.totalCourseCompletionCount}
                </span>
                <span className="font-semibold">
                  ({forcedStateId ? states?.[0]?.name : "India"})
                </span>
              </div>
            </div>
            <IndiaMap
              selectedStateId={forcedStateId ?? statParams.stateId}
              onStateSelect={
                stateAdmin
                  ? undefined
                  : (stateId) => setStatsParams("stateId")(Number(stateId))
              }
              stateOverviewStats={visibleStateOverviewStats}
            />
          </div>

          {loadingStates ||
          loadingDistricts ||
          loadingStateOverviewStats ||
          loadingCountryStats ? (
            <Loader />
          ) : states && districts && visibleStateOverviewStats && countryStats ? (
            <Stats
              statParams={statParams}
              setStatsParams={setStatsParams}
              requiredData={{ states, districts }}
              statsData={{
                stateOverviewStats: visibleStateOverviewStats,
                countryStats,
              }}
              readonlyStateSelection={stateAdmin}
            />
          ) : (
            <div className="text-red font-semibold text-lg">
              An error occurred
            </div>
          )}
        </div>
      ) : fetchStatesErr ? (
        <div className="centered-page text-red-500 font-bold">
          Error fetching states data
        </div>
      ) : fetchCountryStatsErr ? (
        <div className="centered-page text-red-500 font-bold">
          Error fetching stats
        </div>
      ) : (
        <Loader />
      )}
    </AdminLayout>
  );
}
